// Menus by controller (Matt on his Steam Deck, 2026-11-03: "there's no way
// for me to access the sign in button at the top right or the start button
// or really anything else"). Couch Blocks reads its own pads for play, so
// this is a small cursor of its own rather than the padNav house module,
// and App.vue decides WHEN a press is for the menu: in the lobby, paused,
// topped out, or with a panel open - never mid-game.
//
// The d-pad walks a highlight through the things on screen you can press,
// in page order (up = back, down = on); A presses it; B closes whatever
// panel is on top. The highlight is a class, `pad-focus`, styled in App.vue.
//
// The DOM parts need a document; `nextIndex` is pure and tested.

const TARGETS = 'button, a[href], select, input:not([type=hidden]), textarea';

/** The index to move to from `from` in a list of `n` things: wraps, and from nothing goes to the first. */
export function nextIndex(from, n, dir) {
  if (!n) return -1;
  if (from < 0) return 0;
  return (from + (dir === 'up' ? -1 : 1) + n) % n;
}

const visible = (el) => {
  if (!el.isConnected) return null;
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  if (r.bottom < 0 || r.top > innerHeight || r.right < 0 || r.left > innerWidth) return null;
  const s = getComputedStyle(el);
  if (s.visibility === 'hidden' || s.opacity === '0' || s.pointerEvents === 'none') return null;
  return r;
};

/** Is `el` what's under its own middle (not behind a panel's backdrop)? */
const onTop = (el, r) => {
  const x = Math.min(Math.max(r.left + r.width / 2, 0), innerWidth - 1);
  const y = Math.min(Math.max(r.top + r.height / 2, 0), innerHeight - 1);
  const hit = document.elementFromPoint(x, y);
  return !!hit && (hit === el || el.contains(hit) || hit.contains(el));
};

/** Everything pressable on screen right now, in page order. */
export function targets() {
  const out = [];
  for (const el of document.querySelectorAll(TARGETS)) {
    if (el.disabled || el.closest('[inert], [aria-hidden="true"], .pad-nav-ignore')) continue;
    const r = visible(el);
    if (r && onTop(el, r)) out.push(el);
  }
  return out;
}

let current = null;

export const focused = () => (current && current.isConnected ? current : null);

function mark(el) {
  if (current && current !== el) current.classList.remove('pad-focus');
  current = el;
  if (!el) return;
  el.classList.add('pad-focus');
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

export function clearFocus() { mark(null); }

/** Move the highlight up or down the page. */
export function moveFocus(dir) {
  const list = targets();
  const from = focused() ? list.indexOf(current) : -1;
  // The first press lands on the main button if there is one, so Start is
  // one press away; after that it walks.
  if (from < 0) {
    const primary = list.find((el) => el.matches('.primary, .start-button'));
    mark(primary || list[nextIndex(-1, list.length, dir)] || null);
    return;
  }
  mark(list[nextIndex(from, list.length, dir)] || null);
}

/** Press the highlighted thing (a text box takes the keyboard instead). */
export function pressFocus() {
  const el = focused();
  if (!el || !targets().includes(el)) { mark(null); return false; }
  if (el.matches('input:not([type=checkbox]):not([type=radio]), textarea')) { el.focus(); return true; }
  el.click();
  return true;
}

/** Close the panel on top: its Done / Cancel / ✕ button, else Escape. */
export function closeTop() {
  const btn = targets().find((el) => /close|dismiss/i.test(el.getAttribute('aria-label') || el.title || '')
    || /^(✕|×|✖|close|cancel|done|got it)$/i.test((el.innerText || '').trim()));
  if (btn) { btn.click(); mark(null); return true; }
  const target = document.activeElement && document.activeElement !== document.body ? document.activeElement : document;
  for (const type of ['keydown', 'keyup']) target.dispatchEvent(new KeyboardEvent(type, { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true }));
  mark(null);
  return false;
}
